import * as Sentry from "@sentry/nextjs";
import type { Span } from "@sentry/nextjs";
import type { Topic } from "@/lib/odds-screen/live/types";
import {
  classifyLiveToolPath,
  TOOL_LOAD_CLIENT_OBSERVABILITY_ENABLED,
  type LiveToolSurface,
} from "@/lib/performance/tool-load-timing";

export type FirstLiveRowFacts = {
  readonly stream_host?: string;
  readonly live_start_ms?: number;
  readonly ticket_ms?: number;
  readonly socket_open_ms?: number;
  readonly auth_ms?: number;
  readonly catalog_ms?: number;
  readonly rows_snapshot_ms?: number;
  readonly rows_snapshot_chars?: number;
  readonly reconnect_ms?: number;
};

const ROWS_TOPIC: Readonly<Record<LiveToolSurface, Topic["kind"]>> = {
  "game-lines": "optimizer",
  "fantasy-props": "props",
  "low-hold": "lowhold",
  arbitrage: "arbitrage",
  "ev-sniper": "sniper",
  "whale-watch": "whale",
  "esports-discrepancies": "discrepancies",
  "odds-screen": "board",
};

let pending: {
  readonly surface: LiveToolSurface;
  readonly pageload: Span | undefined;
  facts: FirstLiveRowFacts;
} | null = null;

export function armFirstLiveRow(): void {
  const surface = classifyLiveToolPath(window.location.pathname)?.surface;
  if (!TOOL_LOAD_CLIENT_OBSERVABILITY_ENABLED || !surface || document.visibilityState !== "visible") return;
  pending = { surface, pageload: Sentry.getActiveSpan(), facts: {} };
  document.addEventListener("visibilitychange", () => { pending = null; }, { once: true });
}

export function leaveFirstLiveRowTool(href: string): void {
  if (classifyLiveToolPath(new URL(href, window.location.href).pathname)?.surface !== pending?.surface) pending = null;
}

export function noteFirstLiveRow(facts: FirstLiveRowFacts): void {
  if (pending) pending.facts = { ...facts, ...pending.facts };
}

export function noteLiveSnapshot(kind: Topic["kind"], receivedAtMs: number, chars: number): void {
  if (kind === "catalog") noteFirstLiveRow({ catalog_ms: receivedAtMs });
  else if (pending && kind === ROWS_TOPIC[pending.surface]) noteFirstLiveRow({ rows_snapshot_ms: receivedAtMs, rows_snapshot_chars: chars });
}

export function awaitsFirstLiveRow(surface: LiveToolSurface): boolean {
  return pending?.surface === surface;
}

export function reportFirstLiveRow(surface: LiveToolSurface, paintedAtMs: number, board: "rows" | "empty"): void {
  if (pending?.surface !== surface) return;
  const { pageload, facts } = pending;
  pending = null;
  const send = () => Sentry.startInactiveSpan({
    name: "first live row",
    op: "ui.first_live_row",
    startTime: performance.timeOrigin,
    parentSpan: pageload,
    forceTransaction: true,
    attributes: { surface, stream_host: "none", ...facts, board },
  }).end(performance.timeOrigin + paintedAtMs);
  if (!pageload || Sentry.spanToJSON(pageload).timestamp !== undefined) {
    send();
    return;
  }
  const stop = Sentry.getClient()?.on("spanEnd", (span) => {
    if (span !== pageload) return;
    stop?.();
    send();
  });
}
